import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getIsWebCrawler } from "@insite/client-framework/Common/WebCrawler";
import { HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getProductListDataViewProperty } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@tecumseh/theme/design";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: ApplicationState) => {
    const pagination = getProductListDataViewProperty(state, "pagination");
    const correctedQuery = getProductListDataViewProperty(state, "correctedQuery");
    const { productFilters } = state.pages.productList;

    const currentPage = getCurrentPage(state);
    const {
        pages: {
            productList: { isLoading },
        },
    } = state;

    return {
        currentPage,
        totalItemCount: pagination?.totalItemCount,
        query: correctedQuery || productFilters.query,
        location: getLocation(state),
        isLoading,
    };
};

type Props = WidgetProps & HasCategoryContext & ReturnType<typeof mapStateToProps> & OwnProps;

export interface ProductListProductCountStyles {
    wrapper?: InjectableCss;
    countText?: TypographyPresentationProps;
    itemsText?: TypographyPresentationProps;
    queryText?: TypographyPresentationProps;
    categoryName?: InjectableCss;
}

export const productCountStyles: ProductListProductCountStyles = {
    wrapper: {
        css: css`
            display: flex;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-bottom: -10px;
                    `,
                    css``,
                    css``,
                    null,
                    null,
                ])}
        `,
    },
    countText: {
        css: css`
            white-space: nowrap;
            opacity: 1;
            color: ${getColor("secondary")};
            font-family: ${TYPOGRAPHY.fontFamily.base};
            font-size: ${TYPOGRAPHY.size.smaller};
            font-weight: ${TYPOGRAPHY.weight.baseBold};
            font-style: normal;
            letter-spacing: ${TYPOGRAPHY.letterSpacing.smaller};
            margin-top: 17px;
            margin-left: 5px;
        `,
    },
    queryText: {
        css: css`
            white-space: nowrap;
            opacity: 1;
            color: ${getColor("primary")};
            font-family: ${TYPOGRAPHY.fontFamily.base};
            font-size: ${TYPOGRAPHY.size.mLargest};
            font-weight: ${TYPOGRAPHY.weight.baseBold};
            font-style: normal;
            letter-spacing: ${TYPOGRAPHY.letterSpacing.smaller};
            text-align: left;
            margin-top: 7px;
            margin-bottom: 7px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-left: 10px;
                    `,
                    css`
                        margin-left: 10px;
                    `,
                    css`
                        margin-left: 10px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    categoryName: {
        css: css`
            h2 {
                opacity: 1;
                color: ${getColor("primary")};
                font-family: ${TYPOGRAPHY.fontFamily.base};
                font-size: ${TYPOGRAPHY.size.mLargest};
                font-weight: ${TYPOGRAPHY.weight.baseBold};
                font-style: normal;
                letter-spacing: ${TYPOGRAPHY.letterSpacing.smaller};
                text-align: left;
            }
        `,
    },
};

const styles = productCountStyles;

const ProductListProductCount: FC<Props> = ({
    totalItemCount,
    query,
    category,
    currentPage,
    location,
    isLoading,
}: Props) => {
    const isSearchCrawler = getIsWebCrawler();
    const itemsText = (totalItemCount !== 1 ? " " : " ") + (query ? "" : "");

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.categoryName}>
                <Typography variant={isSearchCrawler && currentPage.type === "CategoryDetailsPage" ? "h1" : "h2"}>
                    {category?.properties?.extendedTitle ? translate(category?.properties?.extendedTitle): category?.shortDescription}
                </Typography>
            </StyledWrapper>
            {!isLoading && query && (
                <Typography {...styles.queryText} data-test-selector="productListQueryText">
                    {query}
                </Typography>
            )}
            <Typography {...styles.countText}>({totalItemCount})</Typography>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withCategory(ProductListProductCount)),
    definition: {
        group: "Product List",
        displayName: "Product List Count",
        allowedContexts: ["ProductListPage"],
    },
};

export default widgetModule;
